import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, CircleCheck, FileText, Truck } from 'lucide-react';
import { useUserProfile } from '../context/UserProfileContext';
import { useNotifications } from '../hooks/useNotifications';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import type { AppNotification, NotificationType } from '@iomi/shared';

// ── Helpers ─────────────────────────────────────────────────────────────────

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

const URGENT_TYPES: NotificationType[] = ['quote_response_received'];
const HIGH_TYPES: NotificationType[] = ['quote_lead_assigned', 'move_lead_assigned', 'quote_booked'];

function getAccentClass(type: NotificationType, read: boolean): string {
  if (read) return '';
  if (URGENT_TYPES.includes(type)) return 'border-l-3 border-rose';
  if (HIGH_TYPES.includes(type)) return 'border-l-3 border-plum';
  return 'border-l-3 border-stone';
}

function getIcon(type: NotificationType) {
  switch (type) {
    case 'job_status_changed':
    case 'move_lead_assigned':
      return <Truck size={18} className="text-slate shrink-0" />;
    default:
      return <FileText size={18} className="text-slate shrink-0" />;
  }
}

function getNavigationPath(n: AppNotification): string {
  return `/moves/${n.resourceId}`;
}

// ── Notification Row ────────────────────────────────────────────────────────

function NotificationRow({
  notification,
  onNavigate,
  onMarkRead,
}: {
  notification: AppNotification;
  onNavigate: (path: string) => void;
  onMarkRead: (id: string) => void;
}) {
  const n = notification;
  return (
    <div
      className={`flex items-start gap-3 px-4 py-3 transition-colors
                  ${!n.read ? 'bg-plum/[0.03]' : ''}
                  ${getAccentClass(n.type, n.read)}`}
      style={{ minHeight: '56px' }}
    >
      {/* Icon */}
      <div className="mt-1 shrink-0">{getIcon(n.type)}</div>

      {/* Content — clickable to navigate */}
      <button
        type="button"
        onClick={() => onNavigate(getNavigationPath(n))}
        className="flex-1 min-w-0 text-left hover:opacity-80 transition-opacity"
      >
        <p className={`text-sm leading-tight ${!n.read ? 'font-semibold text-primary-theme' : 'font-medium text-slate'}`}>
          {n.title}
        </p>
        <p className="text-[13px] text-slate mt-0.5 truncate">{n.message}</p>
        <p className="text-[11px] text-slate/70 mt-1">{timeAgo(n.createdAt)}</p>
      </button>

      {/* Mark-read button (only shown for unread) */}
      {!n.read && (
        <button
          type="button"
          onClick={() => onMarkRead(n.id)}
          className="mt-1 shrink-0 p-2 rounded-lg text-slate hover:text-plum
                     hover:bg-plum/5 transition-colors"
          title="Mark as read"
          aria-label="Mark as read"
          style={{ minWidth: '36px', minHeight: '36px' }}
        >
          <CircleCheck size={18} />
        </button>
      )}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function NotificationsPage() {
  const navigate = useNavigate();
  const { activeOrg } = useUserProfile();
  const orgId = activeOrg?.orgId ?? null;
  const {
    notifications, loading, error, unreadCount,
    hasMore, fetchMore, fetchNotifications,
    markRead, markAllRead,
  } = useNotifications(orgId);

  // Fetch full list on mount
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold text-primary-theme">Notifications</h1>
          {unreadCount > 0 && (
            <p className="text-[13px] text-slate mt-0.5">
              {unreadCount} unread
            </p>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            type="button"
            onClick={markAllRead}
            className="flex items-center gap-1.5 px-3 py-2 text-[13px] font-medium text-plum
                       bg-plum/5 hover:bg-plum/10 rounded-lg transition-colors"
          >
            <CheckCheck size={15} />
            Mark all read
          </button>
        )}
      </div>

      {/* Notification list */}
      <div className="border border-stone rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--color-surface, #fff)' }}>
        {loading && notifications.length === 0 ? (
          <div className="py-12 flex justify-center">
            <LoadingSpinner size="md" />
          </div>
        ) : error ? (
          <div className="py-12 text-center">
            <Bell size={32} className="mx-auto mb-3 text-stone" />
            <p className="text-sm text-rose">Failed to load notifications</p>
            <p className="text-[12px] text-slate/70 mt-1">{error}</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="py-12 text-center">
            <Bell size={32} className="mx-auto mb-3 text-stone" />
            <p className="text-sm text-slate">You're all caught up</p>
            <p className="text-[12px] text-slate/70 mt-1">No notifications yet</p>
          </div>
        ) : (
          <>
            {notifications.map(n => (
              <div key={n.id} className="border-b border-stone last:border-b-0">
                <NotificationRow
                  notification={n}
                  onNavigate={(path) => navigate(path)}
                  onMarkRead={markRead}
                />
              </div>
            ))}

            {hasMore && (
              <button
                type="button"
                onClick={fetchMore}
                className="w-full px-4 py-3 text-[13px] font-medium text-plum
                           hover:bg-mist transition-colors text-center border-t border-stone"
              >
                Load more
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
